// Pure helpers for the account settings page (devices, API tokens, sharing).
import type { Schemas } from "@10twenty/api";
import { agoText } from "../live/liveCircle";

type Device = Schemas["Device"];
type ApiToken = Schemas["ApiToken"];

export const TOKEN_NAME_MAX = 100;
export const DEFAULT_TOKEN_NAME = "Home Assistant";

const PLATFORMS: Record<Device["platform"], string> = { android: "Android", web: "Web browser", ios: "iOS" };

export function platformLabel(platform: Device["platform"]): string {
  return PLATFORMS[platform] ?? platform;
}

/** This device first, then most recently seen (never-seen last), then newest. */
export function sortDevices(devices: readonly Device[]): Device[] {
  const seen = (d: Device) => (d.last_seen_at ? Date.parse(d.last_seen_at) : -Infinity);
  return [...devices].sort(
    (a, b) =>
      Number(b.current) - Number(a.current) || seen(b) - seen(a) || Date.parse(b.created_at) - Date.parse(a.created_at),
  );
}

/** "Active just now", "Active 5 min ago", or a date for older activity. */
export function lastActiveText(timestamp: string | undefined, nowMs: number, locale?: string): string {
  if (!timestamp) return "Not used yet";
  const ms = Date.parse(timestamp);
  const ago = agoText(nowMs, ms);
  return ago ? `Active ${ago}` : `Active ${new Date(ms).toLocaleDateString(locale)}`;
}

/** Same as `lastActiveText`, worded for API tokens. */
export function lastUsedText(timestamp: string | undefined, nowMs: number, locale?: string): string {
  if (!timestamp) return "Never used";
  const ms = Date.parse(timestamp);
  const ago = agoText(nowMs, ms);
  return ago ? `Used ${ago}` : `Last used ${new Date(ms).toLocaleDateString(locale)}`;
}

/** Newest first. */
export function sortTokens(tokens: readonly ApiToken[]): ApiToken[] {
  return [...tokens].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
}

/** The circle a token reads. Leaving a circle deletes its tokens, so a miss is a stale list. */
export function tokenCircleName(token: Pick<ApiToken, "circle_id">, circles: readonly { id: string; name: string }[]) {
  return circles.find((c) => c.id === token.circle_id)?.name ?? "Unknown circle";
}

/** How much a token may do, as shown in the list and the create form. */
export function tokenScopeText(scope: ApiToken["scope"]): string {
  return scope === "circle:control" ? "Read + request locations, add places" : "Read only";
}

/** Trimmed token name, or null when empty or too long. */
export function validTokenName(input: string): string | null {
  const name = input.trim();
  return name.length > 0 && name.length <= TOKEN_NAME_MAX ? name : null;
}

/** Account deletion asks the user to type their email; spacing and case don't matter. */
export function deletionConfirmed(input: string, email: string): boolean {
  return input.trim().toLowerCase() === email.trim().toLowerCase() && email.trim() !== "";
}

/** The export's file name from `Content-Disposition`, or one dated `now` (UTC). */
export function exportFileName(contentDisposition: string | null, now: Date): string {
  const match = /filename="([\w.-]+)"/.exec(contentDisposition ?? "");
  return match?.[1] ?? `ten20-export-${now.toISOString().slice(0, 10)}.json`;
}
